import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosError,
  AxiosHeaders,
} from 'axios';
import {
  cachedResponse,
  enqueueWrite,
  flushOutbox,
  isBrowserOffline,
  isNetworkFailure,
  isOfflineQueueable,
  isOfflineReadable,
  isOfflineStartUrl,
  rememberResponse,
  shouldQueueBeforeSending,
  withClientEventId,
} from './offline/offline-api';
import { config } from '@/config';
import { useLanguageStore } from '@/stores/language.store';
import i18n from '@/lib/i18n';
import { isClientLogoutInProgress, purgeAuthTokensAndPersist } from '@/lib/auth-bootstrap';
import { isOptionalAuthContentPath, isPublicOrAuthPath } from '@/lib/public-spa-paths';

interface ApiErrorResponse {
  message?: string;
  statusCode: number;
  errors?: Record<string, string[]>;
  /** Réponses Express 404 : `{ success: false, error: 'Route not found' }` */
  error?: string;
}

const axiosInstance: AxiosInstance = axios.create({
  baseURL: config.API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/** Une seule promesse de refresh pour plusieurs 401 concurrents. */
let refreshSessionPromise: Promise<void> | null = null;

export function waitForSessionRefresh(): Promise<void> {
  return refreshSessionPromise ?? Promise.resolve();
}

function queueSessionRefresh(): Promise<void> {
  if (!refreshSessionPromise) {
    refreshSessionPromise = (async () => {
      const { useAuthStore } = await import('@/stores/auth.store');
      await useAuthStore.getState().refreshAuthToken();
    })().finally(() => {
      refreshSessionPromise = null;
    });
  }
  return refreshSessionPromise;
}

// Request interceptor — attach auth token (ne pas écraser un Authorization déjà défini sur la requête)
axiosInstance.interceptors.request.use(
  (requestConfig) => {
    const existingAuthEarly =
      requestConfig.headers?.Authorization ??
      (requestConfig.headers as Record<string, unknown> | undefined)?.authorization;
    if (
      typeof window !== 'undefined' &&
      !(requestConfig as { skipDefaultAuth?: boolean }).skipDefaultAuth &&
      !existingAuthEarly &&
      isPublicOrAuthPath(window.location.pathname)
    ) {
      const sessionToken = localStorage.getItem('auth_token');
      const keepSession =
        Boolean(sessionToken) && isOptionalAuthContentPath(window.location.pathname);
      if (!keepSession) {
        (requestConfig as { skipDefaultAuth?: boolean }).skipDefaultAuth = true;
      }
    }

    if ((requestConfig as { skipDefaultAuth?: boolean }).skipDefaultAuth) {
      const locale = useLanguageStore.getState().language;
      if (requestConfig.headers) {
        requestConfig.headers['Accept-Language'] = locale;
      }
      if (requestConfig.data instanceof FormData && requestConfig.headers) {
        const h = requestConfig.headers;
        if (h instanceof AxiosHeaders) {
          h.delete('Content-Type');
        } else {
          delete (h as Record<string, unknown>)['Content-Type'];
        }
      }
      return requestConfig;
    }

    const existingAuth =
      requestConfig.headers?.Authorization ??
      (requestConfig.headers as Record<string, unknown> | undefined)?.authorization;
    if (!existingAuth) {
      const token = localStorage.getItem('auth_token');
      if (token && requestConfig.headers) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }
    }

    const locale = useLanguageStore.getState().language;
    if (requestConfig.headers) {
      requestConfig.headers['Accept-Language'] = locale;
    }

    // FormData : ne pas envoyer application/json par défaut (sinon multer ne voit pas le fichier).
    if (requestConfig.data instanceof FormData && requestConfig.headers) {
      const h = requestConfig.headers;
      if (h instanceof AxiosHeaders) {
        h.delete('Content-Type');
      } else {
        delete (h as Record<string, unknown>)['Content-Type'];
      }
    }

    return requestConfig;
  },
  (error) => Promise.reject(error),
);

function formatAxiosApiError(status: number, data: unknown) {
  const body = data as Record<string, unknown> | undefined;
  const nestedError = body?.error;
  const nestedStringMessage = typeof nestedError === 'string' ? nestedError : undefined;
  const nestedMessage =
    nestedError && typeof nestedError === 'object' && nestedError !== null && 'message' in nestedError
      ? String((nestedError as { message: unknown }).message)
      : undefined;
  const topMessage = typeof body?.message === 'string' ? body.message : undefined;
  const nestedCode =
    nestedError && typeof nestedError === 'object' && nestedError !== null && 'code' in nestedError
      ? String((nestedError as { code: unknown }).code)
      : undefined;
  const nestedDetails =
    nestedError && typeof nestedError === 'object' && nestedError !== null && 'details' in nestedError
      ? (nestedError as { details: unknown }).details
      : undefined;
  const formattedError: {
    message: string;
    statusCode: number;
    errors?: Record<string, string[]>;
    code?: string;
    details?: unknown;
  } = {
    message: topMessage || nestedStringMessage || nestedMessage || getDefaultErrorMessage(status),
    statusCode: status,
    errors: body?.errors as Record<string, string[]> | undefined,
  };
  // Code / détails métier : `{ error: { code, details } }` (errorResponse du backend) ou, à défaut, au premier niveau du corps.
  const topCode = typeof body?.code === 'string' ? body.code : undefined;
  const topDetails = body && 'details' in body ? body.details : undefined;
  if (nestedCode || topCode) formattedError.code = nestedCode ?? topCode;
  if (nestedDetails !== undefined) formattedError.details = nestedDetails;
  else if (topDetails !== undefined) formattedError.details = topDetails;
  return formattedError;
}

function purgeSessionAfterUnauthorized(path: string) {
  purgeAuthTokensAndPersist();
  if (isPublicOrAuthPath(path)) {
    queueMicrotask(() => {
      void Promise.all([import('@/stores/auth.store'), import('@/lib/socket')]).then(
        ([{ useAuthStore }, { disconnectSocket }]) => {
          disconnectSocket();
          try {
            useAuthStore.persist.clearStorage();
          } catch {
            /* noop */
          }
          useAuthStore.setState({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        },
      );
    });
  } else if (!path.startsWith('/login')) {
    window.location.href = '/login';
  }
}

// Response interceptor — 401 : tenter refresh puis erreur formatée
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        const path = window.location.pathname;
        const search = window.location.search;
        const isLearnerPlayWithToken =
          /^\/eduflow\/flows\/[^/]+\/play$/.test(path) && search.includes('accessToken=');

        const cfg = error.config;
        const reqUrl = `${cfg?.baseURL ?? ''}${cfg?.url ?? ''}`;
        const isAuthSelf =
          reqUrl.includes('/auth/refresh') ||
          reqUrl.includes('/auth/login') ||
          reqUrl.includes('/auth/register') ||
          reqUrl.includes('/auth/logout');

        const shouldTryRefresh =
          !isClientLogoutInProgress() &&
          !isLearnerPlayWithToken &&
          !isAuthSelf &&
          cfg &&
          !(cfg as { __lyrraRetryAfterRefresh?: boolean }).__lyrraRetryAfterRefresh;

        if (shouldTryRefresh) {
          (cfg as { __lyrraRetryAfterRefresh?: boolean }).__lyrraRetryAfterRefresh = true;
          try {
            await queueSessionRefresh();
            const token = localStorage.getItem('auth_token');
            if (token) {
              if (!cfg.headers) cfg.headers = new AxiosHeaders();
              if (cfg.headers instanceof AxiosHeaders) {
                cfg.headers.set('Authorization', `Bearer ${token}`);
              } else {
                (cfg.headers as Record<string, string>).Authorization = `Bearer ${token}`;
              }
              return axiosInstance.request(cfg);
            }
          } catch (refreshError) {
            // Refresh impossible faute de réseau : garder la session (formations hors ligne, file de progression)
            // et renvoyer une erreur réseau ; le GET retombe sur le cache, le POST part en file.
            if (
              (refreshError instanceof Error && refreshError.message === 'refresh_offline') ||
              (typeof navigator !== 'undefined' && navigator.onLine === false)
            ) {
              return Promise.reject({ message: i18n.t('api.error.network'), statusCode: 0 });
            }
          }
        }

        if (!isLearnerPlayWithToken) {
          purgeSessionAfterUnauthorized(path);
        }

        return Promise.reject(formatAxiosApiError(status, data));
      }

      return Promise.reject(formatAxiosApiError(status, data));
    }

    if (error.request) {
      return Promise.reject({
        message: i18n.t('api.error.network'),
        statusCode: 0,
      });
    }

    return Promise.reject({
      message: error.message || i18n.t('api.error.unexpected'),
      statusCode: 0,
    });
  },
);

function getDefaultErrorMessage(status: number): string {
  const keyByStatus: Record<number, string> = {
    400: 'api.error.badRequest',
    403: 'api.error.forbidden',
    404: 'api.error.notFound',
    409: 'api.error.conflict',
    422: 'api.error.unprocessable',
    429: 'api.error.tooManyRequests',
    500: 'api.error.server',
    502: 'api.error.badGateway',
    503: 'api.error.maintenance',
  };
  const key = keyByStatus[status];
  return key ? i18n.t(key) : i18n.t('api.error.generic', { status });
}

/**
 * Lecture avec repli hors ligne : réponses mémorisées pour les formations de l'apprenant
 * (voir `lib/offline/offline-api.ts`).
 */
const OFFLINE_TIMEOUT = Symbol('offline-timeout');

async function getWithOfflineFallback<T>(
  url: string,
  params?: Record<string, unknown>,
  axiosConfig?: AxiosRequestConfig,
): Promise<T> {
  const cached = await cachedResponse(url, params);
  if (isBrowserOffline() && cached !== undefined) return cached as T;
  const network = axiosInstance.get<T>(url, { params, ...axiosConfig }).then((res) => {
    void rememberResponse(url, params, res.data);
    return res.data;
  });
  try {
    if (cached !== undefined) {
      // Réseau présent mais inutilisable (2G saturée, portail captif) : au-delà de 6 s, la copie locale est servie ;
      // la réponse réseau, si elle arrive, met le cache à jour pour la prochaine fois.
      const timeout = new Promise<typeof OFFLINE_TIMEOUT>((resolve) => window.setTimeout(() => resolve(OFFLINE_TIMEOUT), 6000));
      const first = await Promise.race([network, timeout]);
      if (first === OFFLINE_TIMEOUT) {
        network.catch(() => {});
        return cached as T;
      }
      return first as T;
    }
    return await network;
  } catch (error) {
    if (isNetworkFailure(error)) {
      if (cached !== undefined) return cached as T;
    }
    throw error;
  }
}

/** Progression : envoi direct, ou file d'attente si le réseau manque (rejouée au retour de la connexion). */
async function postWithOfflineQueue<T>(url: string, data?: unknown, axiosConfig?: AxiosRequestConfig): Promise<T> {
  let body = data;
  if (isOfflineStartUrl(url)) {
    // Identifiant de session choisi par le client : identique en ligne et hors ligne.
    const base = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
    const clientSessionId =
      typeof base.clientSessionId === 'string'
        ? base.clientSessionId
        : typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : undefined;
    body = clientSessionId ? { ...base, clientSessionId } : base;
  }
  // Même identifiant d'événement pour l'envoi direct et un éventuel rejeu (le serveur ignore les doublons).
  body = withClientEventId(url, body);
  if (shouldQueueBeforeSending(url)) {
    const queued = (await enqueueWrite(url, body)) as T;
    if (!isBrowserOffline()) void flushOutbox(axiosInstance);
    return queued;
  }
  try {
    return await axiosInstance.post<T>(url, body, axiosConfig).then((res) => res.data);
  } catch (error) {
    if (isNetworkFailure(error)) {
      return (await enqueueWrite(url, body)) as T;
    }
    throw error;
  }
}

export const api = {
  get<T>(url: string, params?: Record<string, unknown>, axiosConfig?: AxiosRequestConfig): Promise<T> {
    if (isOfflineReadable(url)) return getWithOfflineFallback<T>(url, params, axiosConfig);
    return axiosInstance.get<T>(url, { params, ...axiosConfig }).then((res) => res.data);
  },

  post<T>(url: string, data?: unknown, axiosConfig?: AxiosRequestConfig): Promise<T> {
    if (isOfflineQueueable(url)) return postWithOfflineQueue<T>(url, data, axiosConfig);
    return axiosInstance.post<T>(url, data, axiosConfig).then((res) => res.data);
  },

  put<T>(url: string, data?: unknown, axiosConfig?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.put<T>(url, data, axiosConfig).then((res) => res.data);
  },

  patch<T>(url: string, data?: unknown, axiosConfig?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.patch<T>(url, data, axiosConfig).then((res) => res.data);
  },

  delete<T>(url: string, axiosConfig?: AxiosRequestConfig): Promise<T> {
    return axiosInstance.delete<T>(url, axiosConfig).then((res) => res.data);
  },

  upload<T>(url: string, file: File | FormData, onProgress?: (percent: number) => void): Promise<T> {
    const formData = file instanceof FormData ? file : new FormData();
    if (file instanceof File) {
      formData.append('file', file);
    }

    return axiosInstance
      .post<T>(url, formData, {
        /** Ne pas fixer multipart/form-data sans boundary : l’intercepteur supprime Content-Type pour FormData. */
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded * 100) / event.total));
          }
        },
      })
      .then((res) => res.data);
  },
};

export { axiosInstance };
export default api;
